import React, { useState, useEffect } from "react";
import styles from "./sidebar.module.css";
import { changeColorShade } from "../../utils";
import { MdTravelExplore } from "../../onam/icons"; // ONAM glyphs (was react-icons/md)
import BackIcon from "../../assets/BackIcon";
import { useHistory } from "react-router-dom";
import { useSidebar } from "./SidebarContext";
import { findUrlById, getCSSVariable } from "../../utils";
import { useFederation } from "../FederationSelection/FederationContext";
import { FEATURES } from "../../onam/features";
import CurationSide from "../../onam/chrome/CurationSide";

interface SidebarProps {
  authContext: any;
}

const Sidebar: React.FC<SidebarProps> = ({ authContext }) => {
  const [hoveredMenu, setHoveredMenu] = useState(null);

  const [selectedMenu] = useState("Curation");
  const {
    isSubSidebarCollapsed,
    toggleSidebar,
    activeSubItem,
    setActiveSubItem,
    mainMenuItems,
    isSideBarMenuDisabled,
  } = useSidebar();
  const { branding } = useFederation();

  const history = useHistory();

  const [mainMenu] = useState("Discover");

  const [primaryColorCode, setPrimaryColorCode] = useState(
    getCSSVariable("--active-bg-color"),
  );

  const [primaryColorCodeLighter, setPrimaryColorCodeLighter] =
    useState(primaryColorCode);

  useEffect(() => {
    if (primaryColorCode) {
      setPrimaryColorCodeLighter(changeColorShade(primaryColorCode, 0));
    }
  }, [primaryColorCode]);

  useEffect(() => {
    if (branding?.primary_color) {
      setPrimaryColorCode(branding.primary_color);
    }
  }, [branding]);

  function handleSubMenuClick(item) {
    setActiveSubItem(item);
    history.push(`${findUrlById(item.id, mainMenuItems)}`);
  }

  function handleMainMenuClick(item) {
    if (isSubSidebarCollapsed) {
      toggleSidebar();
    }
  }

  // with the ONAM skin, Curation gets ONAM's side panel (onam/chrome/CurationSide.tsx); same pages and permissions
  if (FEATURES.curationSkin) return <CurationSide />;

  return (
    <>
      {isSubSidebarCollapsed && (
        <div
          className={styles.collapsedPrimaryHeader}
          style={{ backgroundColor: primaryColorCode }}
        >
          <MdTravelExplore size={22} color="white" />
          <span className={styles.iconTooltip}>Discover</span>
        </div>
      )}
      {isSubSidebarCollapsed && (
        <div
          className={styles.collapsedHeaderIcon}
          style={{ backgroundColor: primaryColorCode }}
          onClick={toggleSidebar}
        >
          <BackIcon className={styles.expandIcon} onClick={() => {}} />
          <span className={styles.iconTooltip}>Expand</span>
        </div>
      )}
      <aside
        className={`${styles.sidebar} ${
          isSubSidebarCollapsed ? styles.collapsed : ""
        } `}
      >
        <div
          className={`${styles.sidebarWrapper} ${isSubSidebarCollapsed ? styles.collapsed : styles.expanded}`}
          style={
            {
              "--active-bg-color": primaryColorCode,
              "--submenu-bg-color": primaryColorCodeLighter,
            } as React.CSSProperties
          }
        >
          <div
            className={`${styles.sidebarHeader} ${
              isSubSidebarCollapsed ? styles.collapsed : styles.expanded
            }`}
          >
            {!isSubSidebarCollapsed && (
              <>
                <span className={styles.mainMenuText}>{mainMenu}</span>
                <div className={styles.collapseIconWrapper}>
                  <BackIcon
                    className={styles.arrowIcon}
                    onClick={toggleSidebar}
                  />
                  <span className={styles.iconTooltip}>Collapse</span>
                </div>
              </>
            )}
          </div>

          <div className={styles.sidebarContent}>
            <div className={styles.mainSidebar}>
              {mainMenuItems?.map((menu, index) => (
                <div
                  key={index}
                  className={styles.sidebarIconWrapper}
                  onMouseEnter={() => setHoveredMenu(menu.name)}
                  onMouseLeave={() => setHoveredMenu(null)}
                  onClick={() => handleMainMenuClick(menu.name)}
                  style={{
                    backgroundColor:
                      menu.name === "Curation" ? primaryColorCodeLighter : "",
                  }}
                >
                  <div className={styles.sidebarIcon}>
                    <img src={menu.icon} alt={menu.name} />
                  </div>
                  {hoveredMenu === menu.name && (
                    <div className={styles.menuTooltip}>{menu.name}</div>
                  )}
                </div>
              ))}
            </div>

            <div
              className={`${styles.subSidebar} ${
                isSubSidebarCollapsed ? styles.collapsed : styles.expanded
              }`}
            >
              <ul className={styles.menuListIcons}>
                {mainMenuItems[0].subMenu.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li
                      key={item.label}
                      className={`${styles.menuItemIcon} ${
                        activeSubItem === item.label
                          ? styles.menuItemActive
                          : ""
                      } ${isSideBarMenuDisabled[item.id] ? styles.disabledMenu : ""}`}
                      onClick={() => handleSubMenuClick(item)}
                    >
                      {Icon && <Icon size={20} />}
                      <span className={styles.iconTooltip}>{item.label}</span>
                    </li>
                  );
                })}
              </ul>
              <div className={styles.expandedContent}>
                <div className={styles.subHeader}>{selectedMenu}</div>
                <ul className={styles.menuList}>
                  {mainMenuItems[0].subMenu.map((item) => (
                    <li
                      key={item.label}
                      className={`${styles.menuItem} ${
                        activeSubItem === item.label
                          ? styles.menuItemActive
                          : ""
                      } ${isSideBarMenuDisabled[item.id] ? styles.disabledMenu : ""}`}
                      onClick={() => handleSubMenuClick(item)}
                    >
                      {item.label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
